import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import type {
  DashboardStructure,
  DashboardTab,
  MarketApp,
  MarketApps,
  TabEntry
} from '@/features/Dashboard/types';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

// DRY helpers for Dashboard feature. These are pure and framework-agnostic.

/**
 * Returns a safe, effective tab name with a sensible default.
 */
export const getEffectiveTabName = (tabName?: string): string => tabName || TAB_DEFAULT_BASE_NAME;

/**
 * Ensures all MarketApps have unique app and tab IDs and normalizes tiles.
 * This function is idempotent for already-correct data.
 */
export const processMarketApps = (marketApps: MarketApps): MarketApp[] => {
  const usedAppIds = new Set<string>();
  const usedTabIds = new Set<string>();

  return marketApps.map(app => {
    // Widget-like entry (already normalized, passthrough)
    if ('type' in app) {
      return {
        ...app,
        tiles: app.tiles.map(tile => ({
          ...tile
        }))
      };
    }

    let appId = app.id ?? '';
    if (!appId || (appId && usedAppIds.has(appId))) {
      appId = generateUniqueUUID(usedAppIds);
    } else {
      usedAppIds.add(appId);
    }

    const tabs = app.tabs.map(tab => {
      const existing = (tab as any).id as string | undefined;
      let tabId = existing ?? '';
      if (!tabId || usedTabIds.has(tabId)) {
        tabId = generateUniqueUUID(usedTabIds);
      } else {
        usedTabIds.add(tabId);
      }
      return {
        ...tab,
        id: tabId
      };
    });

    return {
      ...app,
      id: appId,
      tabs
    };
  });
};

/**
 * Checks if a tab value is in the new format (has id and content)
 */
export const isTabEntry = (value: any): value is TabEntry => {
  return (
    value &&
    typeof value === 'object' &&
    'id' in value &&
    'content' in value &&
    Array.isArray(value.content)
  );
};

/**
 * Checks if a tab value is in legacy format (just an array)
 */
export const isLegacyTab = (value: any): value is MarketApps => {
  return Array.isArray(value);
};

/**
 * Normalizes a tab entry to the new format with id.
 * If it's already in new format, returns as-is.
 * If it's legacy format, generates an id dynamically.
 */
export const normalizeTabEntry = (tabName: string, value: MarketApps | TabEntry): TabEntry => {
  if (isTabEntry(value)) {
    return value;
  }
  // Legacy format - generate id dynamically
  return {
    id: generateUniqueUUID(new Set()),
    content: value
  };
};

/**
 * Gets MarketApps from a tab entry (handles both legacy and new formats)
 */
export const getTabContent = (value: MarketApps | TabEntry): MarketApps => {
  if (isTabEntry(value)) {
    return value.content;
  }
  return value;
};

/**
 * Normalizes various server payload shapes to a DashboardTab structure.
 * Accepts several legacy formats and returns a consistent representation.
 * Legacy tabs are normalized to include dynamically generated IDs.
 */
export const normalizeDashboardTabs = (
  value: any,
  originalName: string,
  effectiveName: string
): DashboardTab | undefined => {
  let rawTabs: DashboardTab | undefined;

  if (value && typeof value === 'object' && Array.isArray(value.content)) {
    rawTabs = {
      Main: value.content
    } as DashboardTab;
  } else if (value && typeof value === 'object') {
    const isDirectTab = Object.values(value).every(v => Array.isArray(v) || isTabEntry(v));
    if (isDirectTab) {
      rawTabs = value as DashboardTab;
    } else {
      const innerByExpected = value[effectiveName];
      if (innerByExpected && typeof innerByExpected === 'object') {
        rawTabs = innerByExpected as DashboardTab;
      } else if (value[originalName] && typeof value[originalName] === 'object') {
        rawTabs = value[originalName] as DashboardTab;
      } else {
        const firstKey = Object.keys(value)[0];
        const firstVal = firstKey ? value[firstKey] : undefined;
        if (firstVal && typeof firstVal === 'object') {
          const looksLikeTabs = Object.values(firstVal).every(
            (v: any) => Array.isArray(v) || isTabEntry(v)
          );
          if (looksLikeTabs) {
            rawTabs = firstVal as DashboardTab;
          }
        }
      }
    }
  }

  if (!rawTabs) {
    return undefined;
  }

  // Normalize all tabs to new format with IDs
  const normalizedTabs: DashboardTab = {};
  Object.entries(rawTabs).forEach(([tabName, tabValue]) => {
    normalizedTabs[tabName] = normalizeTabEntry(tabName, tabValue);
  });

  return normalizedTabs;
};

/**
 * Builds a server payload from either MarketApps array or a structure-like object.
 * Encapsulates legacy allowances and fallbacks.
 * Returns tabs in new format with id.
 */
export const buildDashboardPayload = (
  input: MarketApps | DashboardStructure,
  dashboardName: string,
  tabName?: string
): DashboardTab => {
  if (Array.isArray(input)) {
    const tab = getEffectiveTabName(tabName);
    return {
      [tab]: {
        id: generateUniqueUUID(new Set()),
        content: input
      }
    } as DashboardTab;
  }

  const asAny = input as any;
  if (asAny && 'content' in asAny && Array.isArray(asAny.content)) {
    const tab = getEffectiveTabName(tabName);
    return {
      [tab]: {
        id: generateUniqueUUID(new Set()),
        content: asAny.content
      }
    } as DashboardTab;
  }

  if (asAny && typeof asAny === 'object') {
    let tabs: DashboardTab;
    if (asAny[dashboardName] && typeof asAny[dashboardName] === 'object') {
      tabs = asAny[dashboardName] as DashboardTab;
    } else {
      tabs = asAny as DashboardTab;
    }

    // Normalize all tabs to new format
    const normalizedTabs: DashboardTab = {};
    Object.entries(tabs).forEach(([name, value]) => {
      normalizedTabs[name] = normalizeTabEntry(name, value);
    });
    return normalizedTabs;
  }

  const tab = getEffectiveTabName(tabName);
  return {
    [tab]: {
      id: generateUniqueUUID(new Set()),
      content: []
    }
  } as DashboardTab;
};
